/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaBuilderAnyModel,
	ISchemaCommentsModel,
	ISchemaConstAnyModel,
	ISchemaConstArrayModel,
	ISchemaConstBooleanModel,
	ISchemaConstEnumModel,
	ISchemaConstFloatModel,
	ISchemaConstIntegerModel,
	ISchemaConstMapModel,
	ISchemaConstObjectModel,
	ISchemaConstPasswordModel,
	ISchemaConstSecretModel,
	ISchemaConstStringModel,
	ISchemaExpressionModel,
	ISchemaIntegrationRefModel,
	ISchemaResponsiveValueModel,
	ISchemaStringWithConstModel,
	ISchemaValueModel,
	TGenericBlueprintSchema,
	TSchemaConstEnumValueSchema,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import { ICodeEditorProps } from "@adapptio/lib-editor-ui";

export enum INSPECTOR_CONTROL_TYPE {
	ADD = "add",
	ANY = "any",
	BOOLEAN = "boolean",
	BUILDER_ANY = "builder-any",
	BUTTON_GROUP = "button-group",
	CODE = "code",
	COLOR = "color",
	COMMENTS = "comments",
	ENUM = "enum",
	EXPAND = "expand",
	EXPRESSION = "expression",
	FLOAT = "float",
	FONT_SIZE = "font-size",
	ICON = "icon",
	INTEGER = "integer",
	PASSWORD = "password",
	RESPONSIVE = "responsive",
	SECRET = "secret",
	SELECT = "select",
	STRING = "string",
	VALUE_TYPE = "value-type",
	ACTION_SELECTOR = "action-selector",
	INTEGRATION_SELECTOR = "integration-selector",
	ROUTE_SELECTOR = "route-selector",
	THEME_SELECTOR = "theme-selector",
	VIEW_SELECTOR = "view-selector"
}

export interface IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE;
	key: string;
	focusModelNodeId?: Set<number>;
}

export interface IInspectorControl_Add extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.ADD;
	model: ISchemaConstArrayModel<TGenericBlueprintSchema> | ISchemaConstMapModel<TGenericBlueprintSchema>;
}

export interface IInspectorControl_Any extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.ANY;
	model: ISchemaConstAnyModel;
}

export interface IInspectorControl_Boolean extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.BOOLEAN;
	model: ISchemaConstBooleanModel;
}

export interface IInspectorControl_BuilderAny extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.BUILDER_ANY;
	model: ISchemaBuilderAnyModel;
}

export interface IInspectorControl_ButtonGroup extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.BUTTON_GROUP;
	model: ISchemaConstObjectModel<TSchemaConstObjectProps>;
}

export interface IInspectorControl_Code extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.CODE;
	model: ISchemaConstStringModel;
	language?: ICodeEditorProps["language"];
	options?: {
		multiLine?: boolean;
		height?: number;
	}
}

export interface IInspectorControl_Color extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.COLOR;
	model: ISchemaStringWithConstModel;
}

export interface IInspectorControl_Comments extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.COMMENTS;
	model: ISchemaCommentsModel;
}

export interface IInspectorControl_Enum extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.ENUM;
	model: ISchemaConstEnumModel<TSchemaConstEnumValueSchema>;
}

export interface IInspectorControl_Expand extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.EXPAND;
	model: ISchemaConstObjectModel<TSchemaConstObjectProps>;
}

export interface IInspectorControl_Expression extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.EXPRESSION;
	model: ISchemaExpressionModel;
}

export interface IInspectorControl_Float extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.FLOAT;
	model: ISchemaConstFloatModel;
}

export interface IInspectorControl_FontSize extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.FONT_SIZE;
	model: ISchemaStringWithConstModel;
}

export interface IInspectorControl_Icon extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.ICON;
	model: ISchemaConstStringModel;
}

export interface IInspectorControl_Integer extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.INTEGER;
	model: ISchemaConstIntegerModel;
}

export interface IInspectorControl_Password extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.PASSWORD;
	model: ISchemaConstPasswordModel;
}

export interface IInspectorControl_Responsive extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.RESPONSIVE;
	model: ISchemaResponsiveValueModel<TGenericBlueprintSchema>;
}

export interface IInspectorControl_Secret extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.SECRET;
	model: ISchemaConstSecretModel;
}

export interface IInspectorControl_Select extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.SELECT;
	model: ISchemaStringWithConstModel;
}

export interface IInspectorControl_String extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.STRING;
	model: ISchemaConstStringModel;
	multiline?: boolean;
}

export interface IInspectorControl_ValueType extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.VALUE_TYPE;
	models: Array<{ model: ISchemaValueModel<TGenericBlueprintSchema>; prefix?: string }>;
}

export interface IInspectorControl_ActionSelector extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.ACTION_SELECTOR;
	model: ISchemaConstStringModel;
}

export interface IInspectorControl_IntegrationSelector extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.INTEGRATION_SELECTOR;
	model: ISchemaIntegrationRefModel;
}

export interface IInspectorControl_RouteSelector extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.ROUTE_SELECTOR;
	model: ISchemaConstStringModel;
}

export interface IInspectorControl_ThemeSelector extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.THEME_SELECTOR;
	model: ISchemaConstStringModel;
}

export interface IInspectorControl_ViewSelector extends IInspectorControl {
	type: INSPECTOR_CONTROL_TYPE.VIEW_SELECTOR;
	model: ISchemaConstStringModel;
}

export type TInspectorControl =
	| IInspectorControl_Add
	| IInspectorControl_Any
	| IInspectorControl_Boolean
	| IInspectorControl_BuilderAny
	| IInspectorControl_ButtonGroup
	| IInspectorControl_Code
	| IInspectorControl_Color
	| IInspectorControl_Comments
	| IInspectorControl_Enum
	| IInspectorControl_Expand
	| IInspectorControl_Expression
	| IInspectorControl_Float
	| IInspectorControl_FontSize
	| IInspectorControl_Icon
	| IInspectorControl_Integer
	| IInspectorControl_Password
	| IInspectorControl_Responsive
	| IInspectorControl_Secret
	| IInspectorControl_Select
	| IInspectorControl_String
	| IInspectorControl_ValueType
	| IInspectorControl_IntegrationSelector
	| IInspectorControl_ActionSelector
	| IInspectorControl_RouteSelector
	| IInspectorControl_ThemeSelector
	| IInspectorControl_ViewSelector;

export type TInspectorControlProps<TInspectorControlType extends IInspectorControl> = {
	[K in keyof Omit<TInspectorControlType, "type" | "key">]: TInspectorControlType[K];
};
